<template>
  <div>
    <el-dialog
      title="面试评价"
      :visible="visible"
      width="650px"
      custom-class="interview-eval-dialog"
      :modal-append-to-body="false"
      :close-on-press-escape="false"
      :close-on-click-modal="false"
      @close="$emit('cancel')"
    >
      <el-form
        ref="evalForm"
        class="interviewEvaluate"
        :model="formModel"
        :rules="rules"
        label-width="100px"
        label-position="top"
      >
        <el-row :gutter="16">
          <el-col :span="24">
            <el-form-item style="margin-bottom: 10px;">
              提示：面试评分可参考
              <el-button type="text" @click="ruleVisible = true">评分维度介绍</el-button>
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :span="12">
            <el-form-item label="技能匹配度" prop="skill_match">
              <NullableScoreInput
                :value="scoreValue('skill_match')"
                :placeholder="`0~${maxProject}分`"
                :min="0"
                :max="maxProject"
                @input="onDimChange('skill_match', $event)"
              />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :span="12">
            <el-form-item label="沟通表达" prop="communication">
              <NullableScoreInput
                :value="scoreValue('communication')"
                :placeholder="`0~${maxPresentation}分`"
                :min="0"
                :max="maxPresentation"
                @input="onDimChange('communication', $event)"
              />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :span="12">
            <el-form-item label="解决问题" prop="problem_solving">
              <NullableScoreInput
                :value="scoreValue('problem_solving')"
                :placeholder="`0~${maxSmall}分`"
                :min="0"
                :max="maxSmall"
                @input="onDimChange('problem_solving', $event)"
              />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :span="12">
            <el-form-item label="工作思路" prop="work_thought">
              <NullableScoreInput
                :value="scoreValue('work_thought')"
                :placeholder="`0~${maxSmall}分`"
                :min="0"
                :max="maxSmall"
                @input="onDimChange('work_thought', $event)"
              />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :span="12">
            <el-form-item label="技术基础" prop="technical_basis">
              <NullableScoreInput
                :value="scoreValue('technical_basis')"
                :placeholder="`0~${maxSmall}分`"
                :min="0"
                :max="maxSmall"
                @input="onDimChange('technical_basis', $event)"
              />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :span="12">
            <el-form-item label="综合得分">
              <el-input :value="totalScoreDisplay" disabled placeholder="自动计算" />
            </el-form-item>
          </el-col>

          <el-col :span="24">
            <el-form-item label="面试评价" prop="text_evaluation">
              <el-input
                :value="scores.text_evaluation"
                type="textarea"
                maxlength="200"
                show-word-limit
                placeholder="请对候选人的专业技能、责任意识、职业稳定性、沟通表达和团队合作等方面进行评价，建议评价内容不低于50字。"
                :autosize="{ minRows: 3, maxRows: 5 }"
                @input="onTextInput"
              />
            </el-form-item>
          </el-col>

          <el-col :span="24">
            <el-form-item label="面试结果" prop="evaluation_result">
              <el-radio-group :value="normalizedEvaluationResult" @input="onResultInput">
                <el-radio :label="1" :disabled="!Number.isFinite(totalScoreNum) || totalScoreNum < 60">通过</el-radio>
                <el-radio :label="0">淘汰</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button size="medium" @click="$emit('cancel')">取消</el-button>
        <el-button type="primary" size="medium" :loading="submitting" @click="onConfirm">确定</el-button>
      </span>
    </el-dialog>

    <ScoreRuleDialog :visible.sync="ruleVisible" :rule-stage="evaluationStage" :modal-append-to-body="false" />
  </div>
</template>

<script>
import ScoreRuleDialog from "./ScoreRuleDialog.vue";
import NullableScoreInput from "./NullableScoreInput.vue";

export default {
  name: "InterviewEvaluationModal",
  components: {
    ScoreRuleDialog,
    NullableScoreInput,
  },
  props: {
    visible: { type: Boolean, default: false },
    scores: { type: Object, default: () => ({}) },
    errors: { type: Object, default: () => ({}) },
    submitting: { type: Boolean, default: false },
    /** 与 interviewEvaluate 中维度文案一致：hr / td / other */
    evaluationStage: {
      type: String,
      default: "other",
    },
  },
  data() {
    return {
      ruleVisible: false,
      maxProject: 35,
      maxPresentation: 20,
      maxSmall: 15,
    };
  },
  watch: {
    visible(v) {
      if (!v) {
        this.ruleVisible = false;
        return;
      }
      this.syncTotalScore();
      this.syncResultWithScore();
    },
    totalScoreNum() {
      if (!this.visible) return;
      this.syncTotalScore();
      this.syncResultWithScore();
    },
  },
  computed: {
    formModel() {
      return this.scores || {};
    },
    totalScoreNum() {
      const n = (k) => {
        const v = this.scores[k];
        if (v === "" || v === null || v === undefined) return 0;
        return Number(v);
      };
      const parts = [
        n("skill_match"),
        n("communication"),
        n("problem_solving"),
        n("work_thought"),
        n("technical_basis"),
      ];
      if (parts.some((x) => !Number.isFinite(x))) return 0;
      return parts.reduce((a, b) => a + b, 0);
    },
    totalScoreDisplay() {
      return String(this.totalScoreNum);
    },
    normalizedEvaluationResult() {
      const raw = this.scores && this.scores.evaluation_result;
      if (raw === 0 || raw === 1) return raw;
      if (raw === "0") return 0;
      if (raw === "1") return 1;
      return null;
    },
    rules() {
      return {
        skill_match: [
          { required: true, message: "请输入", trigger: "change" },
          { validator: (_, v, cb) => this.validateRange(v, 0, 35, cb), trigger: "change" },
        ],
        communication: [
          { required: true, message: "请输入", trigger: "change" },
          { validator: (_, v, cb) => this.validateRange(v, 0, 20, cb), trigger: "change" },
        ],
        problem_solving: [
          { required: true, message: "请输入", trigger: "change" },
          { validator: (_, v, cb) => this.validateRange(v, 0, 15, cb), trigger: "change" },
        ],
        work_thought: [
          { required: true, message: "请输入", trigger: "change" },
          { validator: (_, v, cb) => this.validateRange(v, 0, 15, cb), trigger: "change" },
        ],
        technical_basis: [
          { required: true, message: "请输入", trigger: "change" },
          { validator: (_, v, cb) => this.validateRange(v, 0, 15, cb), trigger: "change" },
        ],
        text_evaluation: [
          { required: true, message: "请输入", trigger: "change" },
          { min: 1, max: 200, message: "最多 200 字", trigger: "change" },
        ],
        evaluation_result: [{ required: true, message: "请选择", trigger: "change" }],
      };
    },
  },
  methods: {
    scoreValue(field) {
      const source = this.scores || {};
      const v = source[field];
      return v === undefined ? null : v;
    },
    validateRange(value, min, max, callback) {
      const n = Number(value);
      if (!Number.isFinite(n)) {
        callback(new Error("请输入"));
        return;
      }
      if (n < min || n > max) {
        callback(new Error(`请输入 ${min}-${max} 之间的分数`));
        return;
      }
      callback();
    },
    onDimChange(field, value) {
      this.$emit("update-score", { field, value });
      this.$emit("revalidate-field", field);
      this.$nextTick(() => {
        this.syncTotalScore();
        this.syncResultWithScore();
        if (this.$refs.evalForm) this.$refs.evalForm.validateField(field);
      });
    },
    onTextInput(v) {
      const value = typeof v === "string" ? v : v && v.target ? v.target.value : "";
      this.$emit("update-score", { field: "text_evaluation", value });
      this.$emit("revalidate-field", "text_evaluation");
    },
    onResultInput(value) {
      this.$emit("update-score", { field: "evaluation_result", value: Number(value) });
      this.$emit("revalidate-field", "evaluation_result");
    },
    syncTotalScore() {
      this.$emit("update-score", { field: "comprehensive_score", value: this.totalScoreNum });
    },
    syncResultWithScore() {
      const next = this.totalScoreNum >= 60 ? 1 : 0;
      if (this.normalizedEvaluationResult === next) return;
      this.$emit("update-score", { field: "evaluation_result", value: next });
      this.$emit("revalidate-field", "evaluation_result");
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },
    onConfirm() {
      if (!this.$refs.evalForm) {
        this.$emit("confirm");
        return;
      }
      this.$refs.evalForm.validate((valid) => {
        if (!valid) return;
        this.$emit("confirm");
      });
    },
  },
};
</script>

<style scoped>

:deep(.el-dialog__body) {
  padding-top: 0px;
  padding-bottom: 0px;
}
::v-deep .el-form--label-top .el-form-item__label {
  line-height: 20px;
}

::v-deep .el-textarea .el-input__count {
  line-height: 1px;
}

::v-deep .el-textarea__inner::placeholder {
  color: #c0c4cc;
  font-weight: 400;
}

::v-deep .el-input-number {
  width: 100%;
}
::v-deep .el-textarea__inner {
  font-family: auto;
}
/* 移动端：宽度 85%、在屏幕中水平垂直居中，整体高度约 60% 视口，表单区域滚动 */
@media (max-width: 768px) {
  ::v-deep .interview-eval-dialog {
    width: 85% !important;
    max-width: 85vw;
    margin: 0 !important;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%) !important;
    height: 60vh;
    max-height: 60vh;
    display: flex;
    flex-direction: column;
    border-radius: 5px;
    overflow: hidden;
  }

  ::v-deep .interview-eval-dialog .el-dialog__header {
    padding: 12px 12px 6px;
    flex-shrink: 0;
  }

  ::v-deep .interview-eval-dialog .el-dialog__title {
    font-size: 16px;
    line-height: 20px;
  }

  ::v-deep .interview-eval-dialog .el-dialog__body {
    padding-left: 10px;
    padding-right: 10px;
    padding-bottom: 6px;
    overflow: auto;
    -webkit-overflow-scrolling: touch;
    flex: 1;
    min-height: 0;
  }

  ::v-deep .interview-eval-dialog .el-dialog__footer {
    padding: 8px 10px 10px;
    flex-shrink: 0;
  }

  ::v-deep .interview-eval-dialog .el-form-item {
    margin-bottom: 10px;
  }

  ::v-deep .interview-eval-dialog .el-form-item__label {
    padding-bottom: 4px;
  }

  ::v-deep .interview-eval-dialog .el-row {
    margin-left: -6px !important;
    margin-right: -6px !important;
  }

  ::v-deep .interview-eval-dialog .el-col {
    padding-left: 6px !important;
    padding-right: 6px !important;
  }

  ::v-deep .interview-eval-dialog .dialog-footer {
    display: flex;
    gap: 10px;
  }

  ::v-deep .interview-eval-dialog .dialog-footer .el-button {
    flex: 1;
  }
}
</style>
